import { useState } from 'react'
import { Download } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingSpinner,
  toast,
} from '@atrvd/saas-ui'
import type dayjs from '@dcp/shared/utils/dayjs'
import { downloadBlob } from '@/lib/downloadBlob'
import type { CashPositionReport } from './cashPositionReport'
import { cashPositionReportFileName } from './cashPositionDownload'
import { buildCashPositionPdf } from './cashPositionPdf'
import { buildCashPositionXlsx } from './cashPositionXlsx'

interface CashPositionDownloadMenuProps {
  /** Snapshot of what the grid shows, taken when a format is picked. */
  getReport: () => CashPositionReport
  orgName: string
  /**
   * The business day the report is run for — the same value the page's "As of"
   * label is built from, so the file name and the in-file date never disagree.
   */
  reportDate: ReturnType<typeof dayjs>
  /** "Uses current filters: {filterText}." */
  filterText: string
  /**
   * True while the grid still shows the previous filters' data (a refetch is in
   * flight), so a file can never pair old figures with the new filter line.
   */
  disabled: boolean
}

const FORMATS = [
  { format: 'pdf', badge: 'PDF', label: 'PDF document' },
  { format: 'xlsx', badge: 'XLSX', label: 'Excel spreadsheet' },
] as const

type ReportFormat = (typeof FORMATS)[number]['format']

/**
 * "Download report" button on Cash Position Summary: offers the report as PDF or
 * XLSX, built from the data already on screen. The writers load their libraries
 * on demand, so importing them here keeps the main bundle light. The button is
 * disabled while a file is being generated; a failure is shown as a toast.
 */
export function CashPositionDownloadMenu({
  getReport,
  orgName,
  reportDate,
  filterText,
  disabled,
}: CashPositionDownloadMenuProps) {
  const [isGenerating, setIsGenerating] = useState(false)

  const download = async (format: ReportFormat) => {
    setIsGenerating(true)
    try {
      const report = getReport()
      const blob = await (format === 'pdf' ? buildCashPositionPdf(report) : buildCashPositionXlsx(report))
      downloadBlob(blob, cashPositionReportFileName(orgName, reportDate, format))
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to generate the report'
      toast.error(`Report download failed: ${message}`)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9"
          aria-label="Download report"
          title="Download report"
          disabled={disabled || isGenerating}
        >
          {isGenerating ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" aria-hidden="true" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Download as
        </DropdownMenuLabel>
        {FORMATS.map(({ format, badge, label }) => (
          <DropdownMenuItem key={format} onSelect={() => void download(format)} className="gap-2.5">
            <span
              aria-hidden="true"
              className="flex h-5 min-w-[46px] items-center justify-center rounded bg-blue-50 px-2 text-[10.5px] font-bold text-blue-700"
            >
              {badge}
            </span>
            {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-xs leading-snug text-muted-foreground">
          Uses current filters: {filterText}.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
